import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { encrypt, decrypt, PHI_FIELDS } from './encryptionService';
import { logAudit, AUDIT_ACTIONS } from './auditService';
import { DEMO_PATIENTS } from '../contexts/DemoContext';

const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

/**
 * Patient Service
 * Handles all patient CRUD operations with PHI encryption and audit logging
 */

const encryptPatientPHI = (patientData) => {
  const encrypted = { ...patientData };
  const fieldsToEncrypt = ['email', 'phone', 'dateOfBirth', 'address', 'emergencyContact'];
  fieldsToEncrypt.forEach(field => {
    if (encrypted[field]) {
      encrypted[field] = encrypt(encrypted[field]);
    }
  });
  return encrypted;
};

const decryptPatientPHI = (patientData) => {
  const decrypted = { ...patientData };
  const fieldsToDecrypt = ['email', 'phone', 'dateOfBirth', 'address', 'emergencyContact'];
  fieldsToDecrypt.forEach(field => {
    if (decrypted[field]) {
      try {
        decrypted[field] = decrypt(decrypted[field]);
      } catch (error) {
        console.error(`Error decrypting field ${field}:`, error);
        decrypted[field] = '[Decryption Error]';
      }
    }
  });
  return decrypted;
};

export const createPatient = async (practiceId, userId, patientData) => {
  if (IS_DEMO) {
    const id = 'demo-new-' + Date.now();
    return { id, ...patientData, createdAt: new Date(), updatedAt: new Date() };
  }

  try {
    const encryptedData = encryptPatientPHI(patientData);
    const patientDoc = {
      ...encryptedData,
      practiceId,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    const docRef = await addDoc(
      collection(db, 'practices', practiceId, 'patients'),
      patientDoc
    );
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.CREATE,
      resourceType: 'patient',
      resourceId: docRef.id,
      details: { patientName: `${patientData.firstName} ${patientData.lastName}` }
    });
    return { id: docRef.id, ...patientDoc };
  } catch (error) {
    console.error('Error creating patient:', error);
    throw error;
  }
};

export const getPatient = async (practiceId, userId, patientId) => {
  if (IS_DEMO) {
    const patient = DEMO_PATIENTS.find(p => p.id === patientId);
    if (!patient) throw new Error('Patient not found');
    return { ...patient };
  }

  try {
    const docRef = doc(db, 'practices', practiceId, 'patients', patientId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) throw new Error('Patient not found');
    const decryptedData = decryptPatientPHI(docSnap.data());
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.READ,
      resourceType: 'patient',
      resourceId: patientId,
      details: { patientName: `${decryptedData.firstName} ${decryptedData.lastName}` }
    });
    return { id: docSnap.id, ...decryptedData };
  } catch (error) {
    console.error('Error getting patient:', error);
    throw error;
  }
};

export const getPatients = async (practiceId, userId) => {
  if (IS_DEMO) {
    return [...DEMO_PATIENTS];
  }

  try {
    const q = query(
      collection(db, 'practices', practiceId, 'patients'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const patients = querySnapshot.docs.map(doc => {
      const decryptedData = decryptPatientPHI(doc.data());
      return { id: doc.id, ...decryptedData };
    });
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.READ,
      resourceType: 'patients',
      resourceId: 'list',
      details: { count: patients.length }
    });
    return patients;
  } catch (error) {
    console.error('Error getting patients:', error);
    throw error;
  }
};

export const updatePatient = async (practiceId, userId, patientId, patientData) => {
  if (IS_DEMO) {
    return { id: patientId, ...patientData, updatedAt: new Date() };
  }

  try {
    const encryptedData = encryptPatientPHI(patientData);
    const updateData = { ...encryptedData, updatedAt: serverTimestamp() };
    const docRef = doc(db, 'practices', practiceId, 'patients', patientId);
    await updateDoc(docRef, updateData);
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.UPDATE,
      resourceType: 'patient',
      resourceId: patientId,
      details: { patientName: `${patientData.firstName} ${patientData.lastName}` }
    });
    return { id: patientId, ...updateData };
  } catch (error) {
    console.error('Error updating patient:', error);
    throw error;
  }
};

export const deletePatient = async (practiceId, userId, patientId, patientName) => {
  if (IS_DEMO) return true;

  try {
    const docRef = doc(db, 'practices', practiceId, 'patients', patientId);
    await deleteDoc(docRef);
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.DELETE,
      resourceType: 'patient',
      resourceId: patientId,
      details: { patientName }
    });
    return true;
  } catch (error) {
    console.error('Error deleting patient:', error);
    throw error;
  }
};

export const searchPatients = async (practiceId, userId, searchTerm) => {
  if (IS_DEMO) {
    const searchLower = searchTerm.toLowerCase();
    return DEMO_PATIENTS.filter(patient => {
      const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
      return fullName.includes(searchLower) ||
        patient.email?.toLowerCase().includes(searchLower) ||
        patient.phone?.includes(searchTerm);
    });
  }

  try {
    const patients = await getPatients(practiceId, userId);
    const searchLower = searchTerm.toLowerCase();
    return patients.filter(patient => {
      const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
      return fullName.includes(searchLower) ||
        patient.email?.toLowerCase().includes(searchLower) ||
        patient.phone?.includes(searchTerm);
    });
  } catch (error) {
    console.error('Error searching patients:', error);
    throw error;
  }
};

export const getPatientsCount = async (practiceId) => {
  if (IS_DEMO) return DEMO_PATIENTS.length;

  try {
    const q = query(collection(db, 'practices', practiceId, 'patients'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error getting patients count:', error);
    return 0;
  }
};

export default {
  createPatient,
  getPatient,
  getPatients,
  updatePatient,
  deletePatient,
  searchPatients,
  getPatientsCount
};
